import { type Prisma, Workload } from "@brookbush/database";

import z from "zod";

import type { PassedCertificationExpanded } from "./certifications.types";
import type { PassedCourseExpanded } from "./courses.types";
import type { UserGeo } from "./geo.types";
import type { ContentItem } from "./globals.types";
import type { UserAvailability } from "./user.types";

export interface ProfilePreview {
	id: string;
	username: string;
	firstName: string | null;
	lastName: string | null;
	imageUrl: string;
	city: string | null;
	country: string | null;
	title: string | null;
	customLocation: string | null;
	customCoordinates?: {
		latitude: number;
		longitude: number;
	};
}

export interface Profile
	extends Omit<
		Prisma.UserGetPayload<{
			include: {
				workExperience: true;
				education: true;
			};
		}>,
		| "externalId"
		| "stripeCustomerId"
		| "ipAddress"
		| "braintreeCustomerId"
		| "stripeConnectId"
	> {
	imageUrl: string;
	isOwner?: boolean;
	passedCertifications: PassedCertificationExpanded[];
	passedCourses: PassedCourseExpanded[];
	likes: ContentItem[];
	visibility: Partial<Record<VisibilityKey, VisibilityStatus>>;
	passedCredits: number;
	availability: UserAvailability;
	geo: UserGeo;
	customCoordinates?: {
		latitude: number;
		longitude: number;
	};
}

export const profileHeaderSchema = z.object({
	firstName: z.string().min(1, "Required"),
	lastName: z.string().min(1, "Required"),
	title: z.string().optional(),

	customLocation: z.string().optional(),

	customCoordinates: z
		.object({
			latitude: z.number(),
			longitude: z.number(),
		})
		.optional(),
});

export type ProfileHeaderSchema = z.infer<typeof profileHeaderSchema>;

export const profileBioSchema = z.object({
	bio: z.string(),
});

export const profileContactFormSchema = z.object({
	message: z.string(),
});

export type ProfileContactFormSchema = z.infer<typeof profileContactFormSchema>;

export const profileAvailabilitySchema = z.object({
	isPublic: z.boolean(),
	isOpenToJobs: z.boolean(),
	isOpenToOnlineSessions: z.boolean(),
	isOpenToClients: z.boolean(),
	isOpenToNetworking: z.boolean(),
});

export type ProfileAvailabilitySchema = z.infer<
	typeof profileAvailabilitySchema
>;

export type ProfileBioSchema = z.infer<typeof profileBioSchema>;

export const profileEducationSchema = z.object({
	degree: z.string().min(1, "Required"),
	fieldOfStudy: z.string().min(1, "Required"),
	schoolName: z.string().min(1, "Required"),
	startedAt: z.string().min(1, "Required"),
	endedAt: z.string().optional(),
	location: z.string().min(1, "Required"),
	description: z.string().optional(),
});

export type ProfileEducationSchema = z.infer<typeof profileEducationSchema>;

export const profileWorkExperienceSchema = z.object({
	title: z.string().min(1, "Required"),
	workload: z.enum([Workload.FULL_TIME, Workload.PART_TIME]),
	companyName: z.string().min(1, "Required"),
	companyUrl: z.string().optional(),
	startedAt: z.string().min(1, "Required"),
	endedAt: z.string().optional(),
	location: z.string().min(1, "Required"),
	description: z.string().optional(),
});

export type ProfileWorkExperienceSchema = z.infer<
	typeof profileWorkExperienceSchema
>;

export enum VisibilityStatus {
	PUBLIC = "public",
	PRIVATE = "private",
}

export type VisibilityKey =
	| Extract<
			keyof Profile,
			| "bio"
			| "education"
			| "workExperience"
			| "passedCourses"
			| "passedCertifications"
			| "likes"
	  >
	| "contactInformation";

export enum ProfileView {
	PREVIEW = "PREVIEW",
	FULL = "FULL",
}
